import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Building2, Wallet } from "lucide-react";

export default function BrokerConfirmation() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initialAction = params.get("action");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [action, setAction] = useState(initialAction === "dispute" ? "dispute" : initialAction === "confirm" ? "confirm" : null);

  // Refund-details state (only shown when the tenant will receive the refund)
  const [refund, setRefund] = useState({
    tenant_payment_method: "bank",
    tenant_bank_name: "",
    tenant_account_number: "",
    tenant_mobile_provider: "MTN",
    tenant_mobile_number: "",
  });

  useEffect(() => { load(); }, []); // eslint-disable-line

  // When info loads, prefill refund form with whatever the broker already submitted
  useEffect(() => {
    if (info) {
      setRefund({
        tenant_payment_method: info.tenant_payment_method || "bank",
        tenant_bank_name: info.tenant_bank_name || "",
        tenant_account_number: info.tenant_account_number || "",
        tenant_mobile_provider: info.tenant_mobile_provider || "MTN",
        tenant_mobile_number: info.tenant_mobile_number || "",
      });
    }
  }, [info]);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/broker/confirm/${token}`);
      setInfo(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid or expired link");
    } finally {
      setLoading(false);
    }
  };

  // Determine when refund details are needed:
  //   - on Dispute (tenant disagrees → likely refund / manual review)
  //   - on Confirm IF broker reported failure (the 90% goes back to tenant)
  const needsRefundDetails =
    action === "dispute" ||
    (action === "confirm" && info?.broker_result === "failed");

  const refundValid = () => {
    if (refund.tenant_payment_method === "bank") {
      return !!(refund.tenant_bank_name.trim() && refund.tenant_account_number.trim());
    }
    return !!(refund.tenant_mobile_provider && refund.tenant_mobile_number.trim());
  };

  const submit = async () => {
    if (!action) return;
    if (needsRefundDetails && !refundValid()) {
      setError("Please provide your bank or mobile-money details so we can refund you.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = needsRefundDetails
        ? {
            tenant_payment_method: refund.tenant_payment_method,
            tenant_bank_name: refund.tenant_payment_method === "bank" ? refund.tenant_bank_name : null,
            tenant_account_number: refund.tenant_payment_method === "bank" ? refund.tenant_account_number : null,
            tenant_mobile_provider: refund.tenant_payment_method === "mobile_money" ? refund.tenant_mobile_provider : null,
            tenant_mobile_number: refund.tenant_payment_method === "mobile_money" ? refund.tenant_mobile_number : null,
          }
        : {};
      await axios.post(`${API}/broker/confirm/${token}?action=${action}`, body);
      setDone(action);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n) => `K ${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  
  if (error && !info) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full"><CardContent className="pt-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold mb-2">Link Not Valid</h2>
          <p className="text-muted-foreground">{error}</p>
        </CardContent></Card>
      </div>
    );
  }

  if (info?.tenant_confirmed || info?.tenant_disputed || done) {
    const final = done || (info.tenant_confirmed ? "confirm" : "dispute");
    const isConfirm = final === "confirm";
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full" data-testid="already-done">
          <CardContent className="pt-6 text-center">
            {isConfirm ? <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" /> : <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />}
            <h2 className="text-2xl font-semibold mb-2">{isConfirm ? "Confirmed" : "Disputed"}</h2>
            <p className="text-muted-foreground">
              {done ? "Thank you. The accounts team has been notified." : `This transaction was already ${final}ed.`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <Building2 className="w-10 h-10 text-primary mx-auto mb-2" />
          <h1 className="text-2xl font-bold">Transaction Confirmation</h1>
          <p className="text-muted-foreground">Hello {info.tenant_name}, please review and confirm.</p>
        </div>

        <Card data-testid="confirmation-details">
          <CardHeader><CardTitle>Transaction Details</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="Broker" value={info.broker_name} />
            <Row label="Reference" value={info.property_reference} />
            <Row label="Description" value={info.description} />
            <Row label="Broker Result" value={(info.broker_result || "").toUpperCase()} highlight={info.broker_result === "success" ? "green" : "red"} />
            <div className="border-t pt-3 mt-3 space-y-1">
              <div className="flex justify-between"><span>Total deposit</span><strong>{fmt(info.deposit_amount)}</strong></div>
              <div className="flex justify-between text-muted-foreground"><span>Commission to platform (10%)</span><span>{fmt(info.commission_amount)}</span></div>
              <div className="flex justify-between text-emerald-700 text-lg"><strong>Payout (90%)</strong><strong>{fmt(info.payout_amount)}</strong></div>
            </div>
          </CardContent>
        </Card>

        <Card className="mt-4">
          <CardHeader><CardTitle>Your Response</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button size="lg" variant={action === "confirm" ? "default" : "outline"} onClick={() => setAction("confirm")} className={action === "confirm" ? "bg-green-600 hover:bg-green-700" : ""} data-testid="confirm-btn">
                <CheckCircle2 className="w-5 h-5 mr-2" />Confirm
              </Button>
              <Button size="lg" variant={action === "dispute" ? "default" : "outline"} onClick={() => setAction("dispute")} className={action === "dispute" ? "bg-red-600 hover:bg-red-700" : ""} data-testid="dispute-btn">
                <XCircle className="w-5 h-5 mr-2" />Dispute
              </Button>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}

            {needsRefundDetails && (
              <div className="border-t pt-4 space-y-3" data-testid="refund-details-section">
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3">
                  <Wallet className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900">Refund details required</p>
                    <p className="text-amber-800">
                      Since the transaction {action === "dispute" ? "is disputed" : "was not successful"}, your <strong>K {(info.payout_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong> will be refunded to you. Please confirm where you would like it sent.
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Refund method *</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setRefund({ ...refund, tenant_payment_method: "bank" })}
                      className={`flex-1 px-3 py-2 border rounded text-sm font-medium ${
                        refund.tenant_payment_method === "bank"
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                      data-testid="refund-method-bank-btn"
                    >
                      Bank Account
                    </button>
                    <button
                      type="button"
                      onClick={() => setRefund({ ...refund, tenant_payment_method: "mobile_money" })}
                      className={`flex-1 px-3 py-2 border rounded text-sm font-medium ${
                        refund.tenant_payment_method === "mobile_money"
                          ? "bg-primary text-white border-primary"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                      data-testid="refund-method-mobile-btn"
                    >
                      Mobile Money
                    </button>
                  </div>
                </div>

                {refund.tenant_payment_method === "bank" ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Bank Name *</label>
                      <Input
                        value={refund.tenant_bank_name}
                        onChange={(e) => setRefund({ ...refund, tenant_bank_name: e.target.value })}
                        placeholder="e.g. Stanbic Bank"
                        data-testid="refund-bank-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Account Number *</label>
                      <Input
                        value={refund.tenant_account_number}
                        onChange={(e) => setRefund({ ...refund, tenant_account_number: e.target.value })}
                        placeholder="Account number"
                        data-testid="refund-account-number"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">Mobile Provider *</label>
                      <select
                        value={refund.tenant_mobile_provider}
                        onChange={(e) => setRefund({ ...refund, tenant_mobile_provider: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md"
                        data-testid="refund-mobile-provider"
                      >
                        <option value="MTN">MTN Mobile Money</option>
                        <option value="Airtel">Airtel Money</option>
                        <option value="Zamtel">Zamtel Kwacha</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Mobile Number *</label>
                      <Input
                        value={refund.tenant_mobile_number}
                        onChange={(e) => setRefund({ ...refund, tenant_mobile_number: e.target.value })}
                        placeholder="+260..."
                        data-testid="refund-mobile-number"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <Button onClick={submit} disabled={!action || submitting} className="w-full" size="lg" data-testid="submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : action ? `Submit ${action === "confirm" ? "Confirmation" : "Dispute"}` : "Choose an action"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              <strong>Confirm</strong> if you agree with the result. <strong>Dispute</strong> if you disagree — the accounts team will review.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const Row = ({ label, value, highlight }) => {
  const colorMap = { green: "text-emerald-600 font-bold", red: "text-red-600 font-bold" };
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className={colorMap[highlight] || "font-medium text-right"}>{value || "—"}</span>
    </div>
  );
};
